import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mic, Square, RotateCcw, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadAudioSubmission } from '../lib/api';

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const options = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
  ];
  return options.find((type) => MediaRecorder.isTypeSupported(type)) || '';
};

const getCompactScriptLines = (scriptText = '') => (
  scriptText
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
);

export function AffirmationAudioRecorder({ token, disabled = false, scriptText = '', onUploaded }) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const recorderSupported = typeof navigator !== 'undefined'
    && Boolean(navigator.mediaDevices?.getUserMedia)
    && typeof MediaRecorder !== 'undefined';

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
  }, [audioUrl]);

  const resetRecording = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl('');
    setAudioFile(null);
    setRecordingSeconds(0);
  };

  const startRecording = async () => {
    if (!recorderSupported || disabled) return;
    try {
      resetRecording();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        const type = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        const extension = type.includes('mp4') ? 'm4a' : 'webm';
        const file = new File([blob], `audio-afirmaciones-${Date.now()}.${extension}`, { type });
        setAudioFile(file);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      timerRef.current = setInterval(() => {
        setRecordingSeconds((value) => value + 1);
      }, 1000);
    } catch (error) {
      toast.error('No se pudo acceder al micrófono. Podés subir un archivo de audio.');
    }
  };

  const stopRecording = () => {
    if (!mediaRecorderRef.current || !isRecording) return;
    mediaRecorderRef.current.stop();
    setIsRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('audio/') && file.type !== 'application/octet-stream') {
      toast.error('Seleccioná un archivo de audio.');
      return;
    }
    resetRecording();
    setAudioFile(file);
    setAudioUrl(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!token || !audioFile) return;
    setIsUploading(true);
    try {
      const result = await uploadAudioSubmission(token, audioFile);
      toast.success('Audio cargado para esta prueba');
      onUploaded?.(result);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'No se pudo subir el audio');
    } finally {
      setIsUploading(false);
    }
  };

  const minutes = Math.floor(recordingSeconds / 60).toString().padStart(2, '0');
  const seconds = (recordingSeconds % 60).toString().padStart(2, '0');
  const compactScriptLines = getCompactScriptLines(scriptText);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Grabación</CardTitle>
        <CardDescription>
          Grabá desde este dispositivo o subí un audio ya grabado.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {scriptText && (
          <div className="sm:hidden rounded-md border bg-muted/30 p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Texto completo a grabar
              </p>
              <span className="shrink-0 rounded-full border bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                Deslizá ↓
              </span>
            </div>
            <div className="relative">
              <div
                className="h-[280px] overflow-y-scroll overscroll-contain pr-3 space-y-1 text-sm leading-5"
                style={{
                  scrollbarGutter: 'stable',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {compactScriptLines.map((line, index) => (
                  <p key={`${line}-${index}`}>{line}</p>
                ))}
              </div>
              <div className="pointer-events-none absolute bottom-0 left-0 right-3 h-7 bg-gradient-to-t from-muted/80 to-transparent" />
            </div>
          </div>
        )}

        {recorderSupported ? (
          <div className="flex flex-wrap items-center gap-3">
            {!isRecording ? (
              <Button type="button" onClick={startRecording} disabled={disabled || isUploading}>
                <Mic className="w-4 h-4 mr-2" />
                Grabar
              </Button>
            ) : (
              <Button type="button" variant="destructive" onClick={stopRecording}>
                <Square className="w-4 h-4 mr-2" />
                Detener
              </Button>
            )}
            <span className="text-sm font-mono text-muted-foreground">{minutes}:{seconds}</span>
            {audioFile && (
              <Button type="button" variant="outline" onClick={resetRecording} disabled={isUploading}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reintentar
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Este navegador no permite grabar directo. Podés subir un audio desde el selector de archivo.
          </p>
        )}

        <div className="space-y-2">
          <Label htmlFor="audio-file">Subir audio</Label>
          <Input
            id="audio-file"
            type="file"
            accept="audio/*"
            capture="microphone"
            onChange={handleFileChange}
            disabled={disabled || isUploading}
          />
        </div>

        {audioUrl && (
          <audio controls src={audioUrl} className="w-full" />
        )}

        <Button
          type="button"
          onClick={handleUpload}
          disabled={disabled || !audioFile || isUploading}
          className="w-full sm:w-auto"
        >
          {isUploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          Subir audio
        </Button>
      </CardContent>
    </Card>
  );
}

export default AffirmationAudioRecorder;
